import CloseIcon from '@mui/icons-material/Close';
import { Box, FormControlLabel, IconButton, Link, Stack, Switch, Tooltip, Typography } from '@mui/material';
import { ChangeEvent, FC } from 'react';
import { TFunction } from 'i18next';
import { RivfldDepth, Station } from '../api/getStations';
import { LiverLineChart } from './LiverLineChart';
import { sendAnalyticsEvent } from '../../../utils/googleAnalytics';

type WaterLevelThreshold = {
  labelKey: string;
  value?: number | null;
  backgroundColor: string;
  color: string;
  lineColor: string;
};

type LiverLineChartData = {
  labels: string[];
  pastData: number[];
  forecastData: (number | null)[];
  latestIndex: string;
  nowIndex: number;
};

type StationDetailModalProps = {
  station: Station;
  rivfldDepth: RivfldDepth | null;
  liverLineChartData: LiverLineChartData | null;
  checked: boolean;
  isPaid?: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onClose: () => void;
  t: TFunction;
};

const StationDetailModal: FC<StationDetailModalProps> = ({
  station,
  rivfldDepth,
  liverLineChartData,
  checked,
  isPaid,
  onChange,
  onClose,
  t,
}) => {
  const waterLevelThresholds: WaterLevelThreshold[] = [
    {
      labelKey: 'floodWarning',
      value: station?.warn2 || null,
      backgroundColor: '#f7d21a',
      color: '#111',
      lineColor: '#e0b800',
    },
    {
      labelKey: 'evacuation',
      value: station?.warn3 || null,
      backgroundColor: '#ff2a2a',
      color: '#fff',
      lineColor: '#ff2a2a',
    },
    {
      labelKey: 'danger',
      value: station?.warn4 || null,
      backgroundColor: '#bb1bb5',
      color: '#fff',
      lineColor: '#bb1bb5',
    },
    {
      labelKey: 'planned',
      value: station?.warn5 || null,
      backgroundColor: '#2f2b39',
      color: '#fff',
      lineColor: '#2f2b39',
    },
  ];
  const availableThresholds = waterLevelThresholds.filter((threshold) => threshold.value != null);

  const latestDepth = liverLineChartData && rivfldDepth?.analysis_results[liverLineChartData.nowIndex - 1]
    ? parseFloat((rivfldDepth.analysis_results[liverLineChartData.nowIndex - 1].depth || 0).toFixed(3))
    : 0;

  const peakDepth = rivfldDepth?.analysis_results?.length
    ? parseFloat((rivfldDepth.analysis_results[rivfldDepth.analysis_results.length - 1].depth || 0).toFixed(3))
    : 0;
  const forecastHours = 39;

  return (
    <Stack sx={{ width: '100vw', height: '100vh', position: 'fixed', top: 0, left: 0, zIndex: 9999 }}>
      <Stack
        sx={{
          width: '90%',
          maxWidth: '600px',
          height: '80%',
          maxHeight: '800px',
          backgroundColor: '#fff',
          position: 'absolute',
          top: '10%',
          left: 0,
          right: 0,
          margin: '0 auto',
          border: '1px solid #ccc',
          borderRadius: '6px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          overflow: 'hidden',
        }}
      >
        <IconButton
          sx={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            backgroundColor: 'green',
            borderRadius: 0,
            '&:hover': {
              backgroundColor: 'darkgreen',
            },
            color: 'white',
          }}
          onClick={onClose}
          color="primary"
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>

        <Stack
          spacing={4}
          px={2}
          sx={{
            height: '100%',
            overflowY: 'auto',
            mt: 6,
            pb: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 'bold' }}>{t('pages.predictionMap.stationDetail.locationInfo')}</Typography>
            <Stack sx={{ border: '1px solid #ccc', p: 2, borderRadius: '4px' }}>
              <Typography>{station.river_name}、{station.station_name}</Typography>
              <Typography>({station.lon}, {station.lat})</Typography>
            </Stack>
          </Box>

          <Box>
            <Typography sx={{ fontWeight: 'bold' }}>{t('pages.predictionMap.stationDetail.riverForecast')}</Typography>

            <Stack direction="row" alignItems="center">
              <Typography mr={1}>{t('pages.predictionMap.stationDetail.pastOnly')}</Typography>
              <Tooltip
                title={!isPaid ? t('pages.predictionMap.paymentModal.title') : null}
                PopperProps={{
                  style: { maxWidth: '300px' },
                }}
              >
                <FormControlLabel
                  control={(
                    <Switch
                      checked={checked}
                      onChange={onChange}
                      color="primary"
                      disabled={!isPaid}
                    />
                  )}
                  label={t('pages.predictionMap.stationDetail.pastAndForecast')}
                />
              </Tooltip>
            </Stack>
            {liverLineChartData && (
              <LiverLineChart
                labels={liverLineChartData.labels}
                pastData={liverLineChartData.pastData}
                forecastData={liverLineChartData.forecastData}
                latestIndex={liverLineChartData.latestIndex}
                thresholdLines={availableThresholds.map((threshold) => ({
                  label: t(`pages.predictionMap.stationDetail.thresholds.${threshold.labelKey}`),
                  value: threshold.value as number,
                  color: threshold.lineColor,
                }))}
                t={t}
              />
            )}
            {rivfldDepth && liverLineChartData && (
              <Stack spacing={1} mt={2}>
                <Typography>{t('pages.predictionMap.stationDetail.latestForecastWaterLevel')}：{latestDepth}m</Typography>
                <Typography>{t('pages.predictionMap.stationDetail.peakForecastWaterLevel', { hours: forecastHours })}：{peakDepth}m</Typography>
              </Stack>
            )}

            {availableThresholds.length > 0 && (
              <Stack
                direction="row"
                spacing={2}
                useFlexGap
                flexWrap="wrap"
                mt={3}
                justifyContent="space-between"
              >
                {availableThresholds.map((threshold) => (
                  <Stack
                    key={threshold.labelKey}
                    spacing={1}
                    sx={{
                      width: { xs: 'calc(50% - 8px)', sm: 'calc(25% - 12px)' },
                      minWidth: '120px',
                      textAlign: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: threshold.backgroundColor,
                        color: threshold.color,
                        borderRadius: '2px',
                        py: 1,
                        px: 0.5,
                        fontWeight: 'bold',
                        fontSize: '0.95rem',
                      }}
                    >
                      {t(`pages.predictionMap.stationDetail.thresholds.${threshold.labelKey}`)}
                    </Box>
                    <Typography sx={{ fontSize: '1.2rem', lineHeight: 1.2 }}>
                      {threshold.value?.toFixed(3)}m
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>

          <Box>
            <Typography sx={{ fontWeight: 'bold' }}>{t('pages.predictionMap.stationDetail.observedWaterLevel')}</Typography>
            <Link
              target="_blank"
              href={`https://www1.river.go.jp/cgi-bin/DspWaterData.exe?KIND=9&ID=${station.global_id}`}
              onClick={() => {
                sendAnalyticsEvent('water_level_detail', {
                  action: 'click_observed_water_level_link',
                  station_id: station.id,
                });
              }}
            >{`https://www1.river.go.jp/cgi-bin/DspWaterData.exe?KIND=9&ID=${station.global_id}`}</Link>
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default StationDetailModal;
